import { PricingInsights } from './PricingInsights.tsx';
import { useEffect, useId, useState } from 'react';
import { can, MIN_REASON } from '@urbanrats/ops-core';
import { DataTable } from '../components/DataTable.tsx';
import { PageHead, Section, StateTag } from '../components/common.tsx';
import { fmtAtomic, fmtTime, useCtx } from '../lib.ts';

interface FinanceDay {
  operatorId: string;
  currency: string;
  scale: number;
  day: string;
  settledStake: string;
  settledPayout: string;
  ggr: string;
  refunds: string;
  betsSettled: number;
}
interface FinanceTotal extends Omit<FinanceDay, 'day'> {
  promoCost: string;
}
interface FinanceView {
  definition: { version: string; text: string };
  asOf: string;
  lastJournalEntryAt: string | null;
  days: FinanceDay[];
  totals: FinanceTotal[];
  balances: { operatorId: string; currency: string; account: string; balance: string }[];
}
interface ReconView {
  ok: boolean;
  checks: { operatorId: string; currency: string; check: string; journal: string; difference: string; ok: boolean }[];
}

const TZS = ['UTC', 'Europe/Istanbul', 'Europe/Berlin', 'America/Sao_Paulo', 'America/New_York', 'Asia/Tokyo'];
const iso = (d: Date) => d.toISOString().slice(0, 10);

/**
 * Journal-based finance (backend only): day buckets in the chosen time zone, per operator and currency,
 * the versioned GGR definition, open balances, and the source → report reconciliation.
 */
export function ServerFinance() {
  const { backend, user } = useCtx();
  const api = backend.server;
  const [tz, setTz] = useState('UTC');
  const [from, setFrom] = useState(() => iso(new Date(Date.now() - 7 * 86_400_000)));
  const [to, setTo] = useState(() => iso(new Date(Date.now() + 86_400_000)));
  const [view, setView] = useState<FinanceView | null>(null);
  const [recon, setRecon] = useState<ReconView | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const q = `?tz=${encodeURIComponent(tz)}&from=${from}T00:00:00Z&to=${to}T00:00:00Z`;
  useEffect(() => {
    if (!api) return;
    let live = true;
    void Promise.all([api.get<FinanceView>(`/v1/admin/reports/finance${q}`), can(user, 'view.reconciliation') ? api.get<ReconView>('/v1/admin/reports/reconciliation') : Promise.resolve(null)]).then(([f, r]) => {
      if (!live) return;
      if (f.ok) {
        setView(f.value);
        setErr(null);
      } else setErr(f.error);
      if (r) setRecon(r.ok ? r.value : null);
    });
    return () => {
      live = false;
    };
  }, [api, q, user]);
  if (!api) return null;
  const m = (x: { scale: number; currency: string }, v: string) => fmtAtomic(BigInt(v), x.scale, x.currency);
  return (
    <Section title="Journal report (server)" testId="server-finance">
      <p className="note">
        {view ? `${view.definition.version}: ${view.definition.text}` : 'Loading the definition…'} Per operator and currency; never summed across currencies. As of {view ? fmtTime(view.asOf) : '…'}
        {view?.lastJournalEntryAt && ` · last journal entry ${fmtTime(view.lastJournalEntryAt)}`}.
      </p>
      <div className="form inline row-form">
        <label>
          Time zone{' '}
          <select value={tz} onChange={(e) => setTz(e.target.value)} data-testid="report-tz">
            {TZS.map((z) => (
              <option key={z}>{z}</option>
            ))}
          </select>
        </label>
        <label>
          From <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label>
          To (exclusive) <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </label>
        {can(user, 'export.csv') && (
          <button type="button" className="btn" onClick={() => void api.download(`/v1/admin/reports/finance.csv${q}`, `finance-${from}-${to}-${tz.replace('/', '_')}.csv`).then((r) => setMsg(r.ok ? r.value : r.error))} data-testid="report-csv">
            Export CSV (server, audited)
          </button>
        )}
      </div>
      {err && <p className="field-error" role="alert">{err}</p>}
      {msg && <p className="note" role="status">{msg}</p>}
      {view && (
        <>
          <DataTable<FinanceTotal>
            testId="server-finance-totals"
            rows={view.totals}
            rowKey={(r) => `${r.operatorId}|${r.currency}`}
            empty="No settled bets in this period."
            columns={[
              { key: 'op', header: 'Operator', cell: (r) => r.operatorId },
              { key: 'cur', header: 'Currency', cell: (r) => r.currency },
              { key: 'st', header: 'Settled stake', numeric: true, cell: (r) => m(r, r.settledStake) },
              { key: 'pa', header: 'Settled payout', numeric: true, cell: (r) => m(r, r.settledPayout) },
              { key: 'ggr', header: 'GGR', numeric: true, cell: (r) => <span className={BigInt(r.ggr) < 0n ? 'bad-text' : ''}>{m(r, r.ggr)}</span> },
              { key: 'promo', header: 'Promo cost (in payout)', numeric: true, cell: (r) => m(r, r.promoCost) },
              { key: 'ref', header: 'Refunds', numeric: true, cell: (r) => m(r, r.refunds) },
              { key: 'n', header: 'Bets settled', numeric: true, cell: (r) => r.betsSettled },
            ]}
          />
          <DataTable<FinanceDay>
            testId="server-finance-days"
            caption={`Days in ${tz}`}
            rows={view.days}
            rowKey={(r) => `${r.operatorId}|${r.currency}|${r.day}`}
            empty="No journal entries in this period."
            columns={[
              { key: 'day', header: 'Day', cell: (r) => r.day },
              { key: 'op', header: 'Operator', cell: (r) => r.operatorId },
              { key: 'cur', header: 'Currency', cell: (r) => r.currency },
              { key: 'st', header: 'Settled stake', numeric: true, cell: (r) => m(r, r.settledStake) },
              { key: 'pa', header: 'Settled payout', numeric: true, cell: (r) => m(r, r.settledPayout) },
              { key: 'ggr', header: 'GGR', numeric: true, cell: (r) => m(r, r.ggr) },
              { key: 'ref', header: 'Refunds', numeric: true, cell: (r) => m(r, r.refunds) },
            ]}
          />
        </>
      )}
      {recon && (
        <div data-testid="server-recon">
          <p>
            Reconciliation (all time): <StateTag state={recon.ok ? 'APPROVED' : 'FAILED'} /> {recon.ok ? 'journal equals its sources' : 'differences found — investigate before closing the period'}
          </p>
          <DataTable
            rows={recon.checks}
            rowKey={(c) => `${c.operatorId}|${c.currency}|${c.check}`}
            empty="No journal entries yet."
            columns={[
              { key: 'op', header: 'Operator', cell: (c) => c.operatorId },
              { key: 'cur', header: 'Currency', cell: (c) => c.currency },
              { key: 'ch', header: 'Check', cell: (c) => c.check },
              { key: 'j', header: 'Journal (atomic)', numeric: true, cell: (c) => c.journal },
              { key: 'd', header: 'Difference', numeric: true, cell: (c) => <span className={c.ok ? '' : 'bad-text'}>{c.difference}</span> },
            ]}
          />
        </div>
      )}
    </Section>
  );
}

interface ModelRow {
  model_id: string;
  source_key: string;
  game_id: string;
  windows: number;
  data_from: string;
  data_to: string;
  status: 'PRICEABLE' | 'NOT_RELIABLE';
  reasons: string[];
  coverage90: number;
  drift: { psi: number; drifted: boolean };
  edge_bps: number;
  created_at: string;
  suspended_at: string | null;
  suspended_reason: string | null;
}
interface ModelDetail {
  model_id: string;
  report: {
    windows: { train: number; validation: number; test: number };
    leakage: { ok: boolean; detail: string };
    counts: { trainMean: number; trainVar: number; testMean: number };
    coverage90: number;
    drift: { psi: number; meanShiftZ: number };
    games: Record<string, { logLoss: number; baselineLogLoss: number; ece: number; backtest: { selection: string; offered: number; wins: number; returnPerUnit: number | null; low: number | null; high: number | null }[] }>;
    reasons: string[];
  };
  quotes: Record<string, { selection: string; p: number; pLow: number; pHigh: number; odds: { numerator: string; denominator: string } | null; reason: string | null }[]>;
}
interface DatasetRow {
  source_key: string;
  calibration_version: number | null;
  calibration_status: string | null;
  origin: string;
  model: string;
  windows: number;
  excluded: number;
  from: string;
  to: string;
}

/** Odds models (backend only): datasets, validation reports, quote tables, suspension. */
export function Pricing() {
  const { backend, user } = useCtx();
  const api = backend.server;
  const [models, setModels] = useState<ModelRow[] | null>(null);
  const [datasets, setDatasets] = useState<DatasetRow[]>([]);
  const [detail, setDetail] = useState<ModelDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [version, setVersion] = useState(0);
  const [reason, setReason] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const id = useId();
  useEffect(() => {
    if (!api) return;
    let live = true;
    void Promise.all([api.get<{ models: ModelRow[] }>('/v1/admin/pricing/models'), api.get<{ datasets: DatasetRow[] }>('/v1/admin/pricing/datasets')]).then(([m, d]) => {
      if (!live) return;
      if (m.ok) setModels(m.value.models);
      else setErr(m.error);
      if (d.ok) setDatasets(d.value.datasets);
    });
    return () => {
      live = false;
    };
  }, [api, version]);
  if (!api)
    return (
      <>
        <PageHead title="Pricing">Odds models live on the backend.</PageHead>
        <p className="empty" data-testid="pricing-demo-note">
          This page needs the UrbanRats backend. The demo uses labelled example odds only; nothing here is simulated.
        </p>
      </>
    );
  if (err) return <p role="alert" className="field-error">{err}</p>;
  if (!models) return <p aria-busy="true">Loading…</p>;
  const hour = String(new Date().getUTCHours());
  return (
    <>
      <PageHead title="Pricing">
        Model price = (1 − edge) / p on a conservative probability, validated out of sample on later windows. A model price is not a return guarantee; unreliable or drifted models never price a round. Automatic counts from a PROVISIONAL line are unvalidated labels.
      </PageHead>
      <PricingInsights />
      <h3 className="pi-models-title">Validated models (per camera)</h3>
      <Section title="Datasets" testId="pricing-datasets">
        <DataTable
          rows={datasets}
          rowKey={(d) => `${d.source_key}|${d.calibration_version}|${d.origin}|${d.model}`}
          empty="No traffic windows recorded yet (node apps/api/src/cli.ts traffic:collect <camera>)."
          columns={[
            { key: 's', header: 'Source', cell: (d) => d.source_key },
            { key: 'c', header: 'Calibration', cell: (d) => (d.calibration_version === null ? '—' : `v${d.calibration_version} ${d.calibration_status}`) },
            { key: 'o', header: 'Origin', cell: (d) => <StateTag state={d.origin} /> },
            { key: 'n', header: 'Windows (excluded)', numeric: true, cell: (d) => `${d.windows} (${d.excluded})` },
            { key: 'r', header: 'Span', cell: (d) => `${fmtTime(d.from)} → ${fmtTime(d.to)}` },
          ]}
        />
      </Section>
      <Section title="Models" testId="pricing-models">
        <DataTable<ModelRow>
          rows={models}
          rowKey={(m) => m.model_id}
          empty="No models trained yet (node apps/api/src/cli.ts pricing:train <source> <game>)."
          columns={[
            {
              key: 'id',
              header: 'Model',
              cell: (m) => (
                <button type="button" className="linkish" onClick={() => void api.get<ModelDetail>(`/v1/admin/pricing/models/${encodeURIComponent(m.model_id)}`).then((r) => r.ok && setDetail(r.value))} data-testid={`model-${m.model_id}`}>
                  {m.model_id}
                </button>
              ),
            },
            { key: 'g', header: 'Game · source', cell: (m) => `${m.game_id} · ${m.source_key}` },
            { key: 'st', header: 'Status', cell: (m) => <StateTag state={m.suspended_at ? 'SUSPENDED' : m.status} /> },
            { key: 'n', header: 'Windows', numeric: true, cell: (m) => m.windows },
            { key: 'cov', header: '90 % coverage', numeric: true, cell: (m) => `${Math.round(m.coverage90 * 100)} %` },
            { key: 'psi', header: 'Drift PSI', numeric: true, cell: (m) => m.drift.psi.toFixed(2) },
            { key: 'why', header: 'Reasons / suspension', cell: (m) => m.suspended_reason ?? (m.reasons.join('; ') || '—') },
          ]}
        />
      </Section>
      {detail && (
        <Section title={`Validation report ${detail.model_id}`} testId="pricing-report">
          <dl className="kv">
            <dt>Split (time-ordered)</dt>
            <dd>
              train {detail.report.windows.train} · validation {detail.report.windows.validation} · test {detail.report.windows.test} — leakage check {detail.report.leakage.ok ? 'passed' : 'FAILED'}: {detail.report.leakage.detail}
            </dd>
            <dt>Counts</dt>
            <dd>
              train mean {detail.report.counts.trainMean.toFixed(2)} (variance {detail.report.counts.trainVar.toFixed(2)}), test mean {detail.report.counts.testMean.toFixed(2)}; 90 % interval covers {Math.round(detail.report.coverage90 * 100)} % of test windows
            </dd>
            <dt>Status</dt>
            <dd>{detail.report.reasons.length ? detail.report.reasons.join('; ') : 'all gates passed'}</dd>
          </dl>
          {Object.entries(detail.report.games).map(([g, v]) => (
            <div key={g}>
              <p className="note">
                {g}: log loss {v.logLoss.toFixed(3)} vs naive baseline {v.baselineLogLoss.toFixed(3)} · calibration error {v.ece.toFixed(3)}. Backtest of the model prices on the test windows (realised return per unit, 90 % block-bootstrap interval):
              </p>
              <DataTable
                rows={v.backtest}
                rowKey={(b) => b.selection}
                empty="—"
                columns={[
                  { key: 's', header: 'Selection', cell: (b) => b.selection },
                  { key: 'o', header: 'Offered · won', numeric: true, cell: (b) => `${b.offered} · ${b.wins}` },
                  { key: 'r', header: 'Return per unit', numeric: true, cell: (b) => (b.returnPerUnit === null ? 'not offered' : `${b.returnPerUnit.toFixed(3)} [${b.low!.toFixed(3)}, ${b.high!.toFixed(3)}]`) },
                ]}
              />
            </div>
          ))}
          <p className="note">Quotes for {hour}:00 UTC (served to rounds whose capture starts in this hour):</p>
          <DataTable
            rows={detail.quotes[hour] ?? []}
            rowKey={(q) => q.selection}
            empty="No quote table (model not PRICEABLE)."
            columns={[
              { key: 's', header: 'Selection', cell: (q) => q.selection },
              { key: 'p', header: 'p [5 %, 95 %]', numeric: true, cell: (q) => `${q.p.toFixed(3)} [${q.pLow.toFixed(3)}, ${q.pHigh.toFixed(3)}]` },
              { key: 'o', header: 'Odds', numeric: true, cell: (q) => (q.odds ? (Number(q.odds.numerator) / Number(q.odds.denominator)).toFixed(2) : `suspended: ${q.reason}`) },
            ]}
          />
          {can(user, 'action.pause') && (
            <div className="form inline">
              <label htmlFor={id}>Reason (min {MIN_REASON} chars, audited)</label>
              <input id={id} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="model-reason" />
              <button
                type="button"
                className="btn"
                disabled={reason.trim().length < MIN_REASON}
                onClick={() =>
                  void api.post(`/v1/admin/pricing/models/${encodeURIComponent(detail.model_id)}/suspend`, { reason: reason.trim() }).then((r) => {
                    setMsg(r.ok ? 'Suspended: new rounds stop pricing from this model; accepted odds stand.' : r.error);
                    setVersion((n) => n + 1);
                  })
                }
                data-testid="model-suspend"
              >
                Suspend model
              </button>
            </div>
          )}
          {msg && <p className="note" role="status">{msg}</p>}
        </Section>
      )}
    </>
  );
}
